export type Guid = string;

export enum TriState {
    No = 0,
    Yes = 1,
    NotApplicable = 2,
}

export enum WorkTimeType {
    Full = 0,
    Part = 1,
}

export interface Address {
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    county: string;
}

export const emptyAddress = (): Address => ({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    county: '',
});

export interface BusinessLicense {
    usesOwnLicense: TriState | null;
    licenseState: string;
    licenseNumber: string;
    hasBeenRevoked: boolean | null;
}

export interface BusinessProfile {
    id: number;
    name: string;
    website: string;
    mailingAddress: Address;
    physicalAddressSameAsMailing: boolean;
    physicalAddress: Address;
    license: BusinessLicense;
    lastEditedUtc: string | null;
}

export interface BorrowerEntity {
    id: number;
    businessProfileId: number;
    legalName: string;
    /** The borrower entity's own ownership figure, per the mockup. */
    ownershipPercent: number | null;
    ein: string;
    yearIncorporated: number | null;
    stateIncorporated: string;
    addressSameAsBusinessProfile: boolean;
    address: Address;
    lastEditedUtc: string | null;
}

export enum OwnerType {
    Person = 1,
    Entity = 2,
    PeopleGroup = 3,
}

interface OwnerBase {
    id: number;
    /** The entity this owner owns a share of. */
    ownedEntityId: number;
    ownershipPercent: number | null;
}

export interface PersonOwner extends OwnerBase {
    ownerType: OwnerType.Person;
    personId: number | null;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    role: string;
    officerTitle: string;
    phone: string;
    email: string;
    workTime: WorkTimeType | null;
    isWillingGuarantor: boolean | null;
}

export interface EntityOwner extends OwnerBase {
    ownerType: OwnerType.Entity;
    legalName: string;
    ein: string;
    yearIncorporated: number | null;
    stateIncorporated: string;
}

export interface PeopleGroupOwner extends OwnerBase {
    ownerType: OwnerType.PeopleGroup;
    investsInOwnName: boolean;
    noSingleOwnerOverTwentyPercent: boolean;
}

export type Owner = PersonOwner | EntityOwner | PeopleGroupOwner;

export interface OwnerPercentage {
    ownerId: number;
    ownershipPercent: number | null;
}

export const ownerDisplayName = (owner: Owner): string => {
    switch (owner.ownerType) {
        case OwnerType.Person:
            return [owner.firstName, owner.middleName, owner.lastName, owner.suffix]
                .filter(Boolean)
                .join(' ');
        case OwnerType.Entity:
            return owner.legalName;
        case OwnerType.PeopleGroup:
            return 'Group of People';
    }
};

export interface Officer {
    id: number;
    entityId: number;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    officerTitle: string;
    phone: string;
    email: string;
    workTime: WorkTimeType | null;
    isWillingGuarantor: boolean | null;
    lastEditedUtc: string | null;
}

export interface KeyEmployee {
    id: number;
    entityId: number;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    title: string;
    phone: string;
    email: string;
    workTime: WorkTimeType | null;
    lastEditedUtc: string | null;
}

export interface BusinessReference {
    id: number;
    businessProfileId: number;
    referenceType: string;
    companyName: string;
    contactName: string;
    phone: string;
    email: string;
    lastEditedUtc: string | null;
}

export interface OrgChartNode {
    key: string;
    label: string;
    nodeType: OwnerType | 'business' | 'borrowerEntity';
    ownershipPercent: number | null;
    canEdit: boolean;
    canDelete: boolean;
    children: OrgChartNode[];
}

export interface WizardState {
    businessProfileId: number | null;
    activeEntityId: number | null;
    lastVisitedStepId: string | null;
    completedStepIds: string[];
}
